import { GetServerSideProps } from 'next/types';
import nookies from 'nookies';
import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import { IntegrationType } from '@/components/dtcPartnerships/shared/types';
import Layout from '@/components/layout/LayoutWrapper';
import SimplifiedSignup from '@/components/SimplifiedSignup';
import { partnerConfigGenerator } from '@/components/SimplifiedSignup/configs/Partnerships/generatePartnerConfig';
import { shapePartnerForParams } from '@/components/SimplifiedSignup/configs/Partnerships/shapePartnerForParams';
import { SimplifiedSignupContextProvider } from '@/components/SimplifiedSignup/Context';
import { InitRequest, usePartnerState } from '@/hooks/store';
import { getPartner } from '@/server/initState/getPartner';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { getPagesRouterSsrDirectOpts } from '@/utils/experiments/getPagesRouterSsrDirectOpts';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

const Subscribe = (): JSX.Element => {
	const partner = usePartnerState();
	const { formatMessage } = useIntl();

	const partnerFlow = partnerConfigGenerator(partner, formatMessage);
	return (
		<Layout title={`Calm + ${partner?.name}`} pageName={`dtc-partnership-${partner?.name}}`}>
			<SimplifiedSignupContextProvider flowConfig={partnerFlow}>
				<SimplifiedSignup />
			</SimplifiedSignupContextProvider>
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async context => {
	const cookies = nookies.get({ req: context.req });
	const deviceId = cookies['x-device-id'] ?? '';
	const userData = getUserData({ req: context.req, deviceId });
	const ssrDirectOpts = await getPagesRouterSsrDirectOpts(context);
	const partner = await getPartner(context.query.partnerSlug, userData, ssrDirectOpts);
	const initialState = await getInitialServerState(
		context,
		[InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
		shapePartnerForParams(partner),
		ssrDirectOpts ?? 'skip-gate',
	);

	// Check if the offer is valid
	// first check if the offer is valid from Partner Portal
	if (partner?.offer_details && 'promotion_active' in partner.offer_details) {
		const isCodeless = partner.integration_type === IntegrationType.DTC_CODELESS;
		const hasCoupon = initialState?.prices?.coupon;
		const isCouponValid = initialState?.prices?.coupon?.valid;
		partner.offer_details.promotion_active =
			isCodeless && hasCoupon ? Boolean(isCouponValid) : partner.offer_details.promotion_active;
		if (initialState) initialState.purchaseParams.promotion = partner.name.toLowerCase();
	}
	const initialStateWithPartner = {
		...initialState,
		partner,
	};

	return {
		props: {
			initialState: initialStateWithPartner,
			rawPartner: partner,
		},
		notFound: !partner,
	};
};

export default Subscribe;
